/* eslint-disable react/no-multi-comp */

import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@app/i18n/config';
import * as R from 'ramda';

import connect from '@app/utils/connect';

import sendNotification from '@app/actions/notifications/send';

import {
    PREMIUM_COLLECTIONS,
    PREMIUM_PLACEMENT_TYPES,
    INTERNATIONAL_BROADCAST_VIEW,
    GROUP_BY_BROADCAST_METADATA,
} from '@app/constants/config';

import getTruthyKeys from '@app/utils/getTruthyKeys';

import { Client } from '@app/types/api';

import {
    Flex,
    Typography,
    Button,
    Spin,
    theme,
    Collapse,
    ConfigProvider,
    Tooltip,
    Col,
    Row,
} from '@app/ui';

import { useInsightsContext } from './InsightsContext';
import usePrevious from '@app/components/hooks/usePrevious';
import { getConfigByGroupType, SORTING_ORDER } from '@app/constants/insights';
import abbreviateAccount from '@app/utils/abbreviateAccount';
import { Line, Column, LineConfig, ColumnConfig } from '@ant-design/charts';
import useDebounce from '@app/components/hooks/useDebounce';
import combineActions from '@app/utils/combineActions';
import { Notification } from '@app/components/notifications/Notifications';

type InsightsProps = {
    client: Client;
    filters: any;
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
};

export const DATA_COMPARING_PAGE_SIZE = 8;
const DATA_LIMIT = 5;

const InsightsChart = ({
    client,
    filters,
    sendNotification,
}: InsightsProps) => {
    const { t } = useTranslation('measurement');
    const {
        groupByOptions,
        setIsSelectingFromTable,
        setSelectedMetric,
        selectedMetric: nonComparingMetric,
        compareMetric: comparingMetric,
        sortingOrder,
        setSortingOrder,
        hasCompareDates,
        data,
        groupBy,
        isLoading,
        dateRange,
        compareDateRange,
        yAxis,
        yAxisMap,
        page,
    } = useInsightsContext();

    const { rightsholders } = filters;

    const {
        token: { colorPrimary },
    } = theme.useToken();

    const [activeKey, setActiveKey] = useState<string[] | null>(null);

    const compareLabels = hasCompareDates
        ? [dateRange, compareDateRange]
        : null;

    const visualizationGroupByOptions = useMemo(() => {
        const result = [...groupByOptions];
        const permissions = client.permissions || [];

        if (permissions.includes(PREMIUM_COLLECTIONS)) {
            const index =
                result.findIndex(el => {
                    return (
                        el.label ===
                        t('groupByOptions.accountOwnershipType', {
                            ns: 'common',
                        })
                    );
                }) + 1;
            result.splice(index, 0, {
                label: t('groupByOptions.collection', { ns: 'common' }),
                value: 'collection',
            });
        }

        if (permissions.includes(PREMIUM_PLACEMENT_TYPES)) {
            const index =
                result.findIndex(el => {
                    return (
                        el.label ===
                        t('groupByOptions.rightsholderPlacement', {
                            ns: 'common',
                        })
                    );
                }) + 1;
            result.splice(index, 0, {
                label: t('groupByOptions.placementType', { ns: 'common' }),
                value: 'placementType',
            });
        }

        if (permissions.includes(INTERNATIONAL_BROADCAST_VIEW)) {
            result.push({
                label: t('groupByOptions.country', { ns: 'common' }),
                value: 'country',
            });
        }

        if (permissions.includes(GROUP_BY_BROADCAST_METADATA)) {
            result.push({
                label: t('groupByOptions.seasonType', { ns: 'common' }),
                value: 'seasonType',
            });
            result.push({
                label: t('groupByOptions.gameType', { ns: 'common' }),
                value: 'gameType',
            });
            result.push({
                label: t('groupByOptions.homeTeam', { ns: 'common' }),
                value: 'homeTeam',
            });
            result.push({
                label: t('groupByOptions.awayTeam', { ns: 'common' }),
                value: 'awayTeam',
            });
        }

        return result;
    }, [groupByOptions, client.permissions]);

    const visualizationGroupBy = useMemo(
        () => getTruthyKeys(groupBy),
        [groupBy],
    );

    const [isLoadingChart, setIsLoadingChart] = useState(true);
    const isComparingData =
        Array.isArray(compareLabels) && compareLabels.length > 0;
    const selectedMetric = isComparingData
        ? comparingMetric
        : nonComparingMetric;
    const previousIsComparingData = usePrevious(isComparingData);
    const configByGroupType = getConfigByGroupType(t);
    const groupOption = visualizationGroupBy[0];
    const xOptions = R.path([groupOption], configByGroupType);
    const visualizationType = xOptions?.type || 'line';
    const chartRef = useRef<any>(null);
    const collapseContentRef = useRef<HTMLDivElement>(null);

    const debouncedHeight = useDebounce(
        collapseContentRef.current?.clientHeight,
        100,
    );

    const isCollapseFullyOpen =
        collapseContentRef.current?.clientHeight === debouncedHeight &&
        collapseContentRef.current?.clientHeight > 0;

    useEffect(() => {
        if (!isComparingData && previousIsComparingData) {
            setSelectedMetric(yAxis);
            setSortingOrder(SORTING_ORDER.DESC);
        }
    }, [isComparingData]);

    const noVisuals = useMemo(() => {
        return (
            (!isComparingData ? data?.length <= 1 : data.length <= 0) ||
            visualizationGroupBy.length === 0 ||
            visualizationGroupBy.length > 1
        );
    }, [data, visualizationGroupBy, isComparingData]);

    const { name: yName, yAxisLabel } = yAxisMap[selectedMetric];
    const xName = visualizationGroupByOptions.find(
        el => el.value === groupOption,
    )?.label;

    const title = useMemo(() => {
        return t('dataExplorer.chart.titleTemplate', {
            yName: yAxisLabel ? yAxisLabel : yName,
            xName,
            ns: 'common',
        });
    }, [
        selectedMetric,
        visualizationGroupByOptions,
        groupOption,
        t,
        yName,
        xName,
    ]);

    // English title for PNG filename (always in English)
    const englishTitleForFilename = useMemo(() => {
        const tEnglish = i18n.getFixedT('en-US', ['common', 'measurement']);

        // Get English yName
        const englishYNameMap: Record<string, string> = {
            exposures: 'Exposures',
            duration: 'Duration',
            impressions: 'Impressions',
            videoViews: 'Video Views',
            engagements: 'Engagements',
            fullMediaValue: 'Full Media Value',
            averageMvp: 'Average MVP',
            sponsorMediaValue: 'Sponsor Media Value',
            postCount: 'Post Count',
            broadcastCount: 'Broadcast Count',
        };
        const englishYName = englishYNameMap[selectedMetric] || yName;

        // Get English xName
        const englishXName = tEnglish(`groupByOptions.${groupOption}`, {
            ns: 'common',
            defaultValue: xName || groupOption,
        });

        return `${englishYName} by ${englishXName}`;
    }, [selectedMetric, groupOption, yName, xName]);

    const yAxisTitle = useMemo(() => {
        return yAxisLabel ? yAxisLabel : yName;
    }, [yAxisLabel, yName]);

    // const subTitle = isComparingData ? 'Date Range Comparison' : null;

    const download = e => {
        try {
            e.stopPropagation();
            e.preventDefault();
            const downloadTitle = englishTitleForFilename.replace(/\s/g, '_');
            const chart = chartRef.current;
            if (chart) {
                chart?.downloadImage(`${downloadTitle}.png`);
                sendNotification(
                    t('dataExplorer.notifications.chartDownloaded'),
                    {
                        context: 'success',
                    },
                );
            } else {
                sendNotification(t('dataExplorer.notifications.noChart'), {
                    context: 'info',
                });
            }
        } catch (e) {
            sendNotification(t('dataExplorer.notifications.downloadError'), {
                context: 'error',
            });
        }
    };

    const chartConfig: LineConfig | ColumnConfig = useMemo(() => {
        let truncatedData = data;
        const xOptions = R.path([groupOption], configByGroupType);
        if (!xOptions || !data) return null;

        if (selectedMetric && sortingOrder) {
            const sorted = data.sort(
                (a, b) =>
                    (b[selectedMetric] - a[selectedMetric]) * sortingOrder,
            );

            const pageSize = isComparingData
                ? DATA_COMPARING_PAGE_SIZE
                : DATA_LIMIT;
            const currentPageIndex = isComparingData
                ? page * DATA_COMPARING_PAGE_SIZE
                : 0;
            const currentPageLastIndex = currentPageIndex + pageSize;
            truncatedData = R.slice(
                currentPageIndex,
                groupOption === 'date' ? sorted.length : currentPageLastIndex,
                groupOption === 'date'
                    ? R.sortBy(d => new Date(d.date), sorted)
                    : sorted,
            );
        }

        const wrapLabel = (label: string, maxLength = 20): string => {
            if (!label) return '';

            const words = label.split(' ');
            const lines: string[] = [];
            let currentLine = '';

            for (const word of words) {
                // If adding the word would exceed the maxLength, push current line to lines
                if (
                    (currentLine + (currentLine ? ' ' : '') + word).length >
                    maxLength
                ) {
                    if (currentLine) lines.push(currentLine);
                    currentLine = word;
                } else {
                    currentLine += (currentLine ? ' ' : '') + word;
                }
            }

            // Push the last line
            if (currentLine) lines.push(currentLine);

            return lines.join('\n');
        };

        const labels = truncatedData.map(d => {
            let label = '';

            const rhId = d.rightsholderId;
            let rh = null;

            switch (xOptions.propName) {
                case 'socialAccountGroupName':
                    label =
                        d.socialAccountGroupName ||
                        t('groupByOptions.socialAccountGroupUndefined', {
                            ns: 'common',
                        });
                    break;
                case 'collaboratingAccounts': {
                    const raw = d.collaboratingAccounts;
                    const asString = raw == null ? '' : String(raw).trim();
                    const COLLAB_LABEL_MAX = 20;
                    label =
                        asString !== ''
                            ? asString.length > COLLAB_LABEL_MAX
                                ? `${asString.slice(0, COLLAB_LABEL_MAX - 1)}…`
                                : asString
                            : t('groupByOptions.collaboratingAccountsNone', {
                                  ns: 'common',
                              });
                    break;
                }
                case 'accountUsername':
                    label = abbreviateAccount(
                        d.accountUsername,
                        d.socialPlatformName,
                    );
                    break;
                case 'rightsholderPlacementName':
                    rh = rightsholders.list?.[rhId];
                    label = `${d[xOptions.propName]} - ${rh?.name}`;
                    break;

                default:
                    label = `${d[xOptions.propName]}`;
            }
            return wrapLabel(label);
        });

        let datasets = [];

        if (isComparingData) {
            datasets = compareLabels.flatMap((label, i) => {
                const key = i === 0 ? 'dateRange' : 'compareDateRange';
                return truncatedData.map((d, index) => ({
                    Label: labels[index],
                    Value: d[key],
                    Comparison: label,
                }));
            });
        } else {
            const values = truncatedData.map(d => d[selectedMetric]);

            datasets = labels.map((label, i) => {
                return {
                    [xOptions.propName || 'Label']: label,
                    [yName]: values[i],
                };
            });
        }

        const { chartFormat, format } = yAxisMap[selectedMetric];
        const formatter = (value: number) => {
            if (chartFormat) {
                return chartFormat(value);
            }
            if (format) {
                return format(value);
            }
            return value;
        };

        const subtitle = isComparingData
            ? `${compareLabels[0]} ${t('dataExplorer.chart.comparisonConnector', { ns: 'common' })} ${compareLabels[1]}`
            : `${dateRange}`;

        const colors = isComparingData ? ['#5B8FF9', '#5AD8A6'] : ['#5B8FF9'];

        const baseConfig = {
            theme: {
                view: {
                    viewFill: '#ffffff',
                    plotFill: '#ffffff',
                },
            },
            data: datasets,
            xField: isComparingData ? 'Label' : xOptions.propName || 'Label',
            yField: isComparingData ? 'Value' : yName,
            seriesField: isComparingData ? 'Comparison' : undefined,
            colorField: isComparingData ? 'Comparison' : undefined,
            legend: isComparingData ? false : undefined,
            animation: false,
            autoFit: true,
            height: 450,
            inset: 0,
            marginBottom: 0,
            meta: {
                [yName]: {
                    alias: yName,
                },
            },
            title: {
                title,
                subtitle,
            },
            tooltip: {
                channel: 'y',
                valueFormatter: formatter,
            },
            interactions: [{ type: 'element-highlight' }],
            axis: {
                x: {
                    labelFontSize: visualizationType === 'bar' ? 16 : 12,
                    labelFill: '#5E6066',
                    labelFontWeight:
                        visualizationType === 'bar' ? 500 : undefined,
                    labelOpacity: 1,
                    labelTransform:
                        visualizationType === 'line'
                            ? 'rotate(-45)'
                            : 'rotate(0)',
                    labelFormatter: (value: string) => {
                        if (groupOption === 'date') {
                            const d = new Date(value);
                            if (!isNaN(d.getTime())) {
                                const mm = `${d.getMonth() + 1}`.padStart(
                                    2,
                                    '0',
                                );
                                const dd = `${d.getDate()}`.padStart(2, '0');
                                return `${mm}/${dd}`;
                            }
                            const parts = value.split('-');
                            if (parts.length >= 3)
                                return `${parts[1]}/${parts[2].slice(0, 2)}`;
                        }
                        return value;
                    },
                },
                y: {
                    title: yAxisTitle,
                    labelFormatter: formatter,
                    titleFontSize: 16,
                    titleFill: '#5E6066',
                    titleStroke: 'transparent',
                    labelFontSize: 16,
                    labelFill: '#5E6066',
                    labelOpacity: 1,
                    tickLine: true,
                    grid: true,
                    line: true,
                },
            },
            scale: {
                color: {
                    range: colors,
                },
            },
        };

        setIsLoadingChart(false);

        return baseConfig;
    }, [
        data,
        page,
        selectedMetric,
        sortingOrder,
        isComparingData,
        isCollapseFullyOpen,
        title,
        visualizationType,
    ]);

    useEffect(() => {
        if (!noVisuals) {
            setActiveKey(['1']);
        } else {
            setActiveKey(null);
        }
    }, [noVisuals]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Collapse: {
                        headerBg: '#FFF',

                        headerPadding: '12px 16px 12px 24px',
                    },
                },
            }}
        >
            <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <div className="data-explorer-chart">
                        <Collapse
                            expandIconPosition="end"
                            activeKey={activeKey}
                            onChange={keys => setActiveKey(keys as string[])}
                            collapsible={noVisuals ? 'disabled' : undefined}
                            expandIcon={({ isActive }) => (
                                <i
                                    className={`fa-solid fa-chevron-${
                                        isActive ? 'up' : 'down'
                                    }`}
                                    style={{
                                        fontSize: '16px',
                                        color: '#515252',
                                    }}
                                />
                            )}
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <Tooltip
                                            title={
                                                visualizationGroupBy.length >
                                                    1 &&
                                                t(
                                                    'dataExplorer.visualization.multiGroupByTooltip',
                                                )
                                            }
                                            placement="topRight"
                                        >
                                            <Flex
                                                justify="space-between"
                                                align="center"
                                            >
                                                <Typography.Title
                                                    level={4}
                                                    style={{
                                                        margin: 0,
                                                        color: !noVisuals
                                                            ? '#1f1f1f'
                                                            : '#bcbcbc',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    {t(
                                                        'dataExplorer.visualization.title',
                                                    )}
                                                </Typography.Title>
                                                <Button
                                                    onClick={download}
                                                    className="-m-l-2"
                                                    data-test="insights-download"
                                                    type="primary"
                                                    color="primary"
                                                    variant="solid"
                                                    disabled={noVisuals}
                                                >
                                                    <i className="fa-regular fa-download -m-r-1" />
                                                    {t(
                                                        'dataExplorer.visualization.downloadPng',
                                                    )}
                                                </Button>
                                            </Flex>
                                        </Tooltip>
                                    ),
                                    children: (
                                        <Flex gap={16} ref={collapseContentRef}>
                                            {!isComparingData && (
                                                <Flex
                                                    vertical
                                                    style={{ minWidth: 200 }}
                                                >
                                                    <ConfigProvider
                                                        theme={{
                                                            token: {
                                                                colorPrimaryHover:
                                                                    colorPrimary,
                                                            },
                                                            components: {
                                                                Button: {
                                                                    colorText:
                                                                        '#515252',
                                                                },
                                                            },
                                                        }}
                                                    >
                                                        {Object.entries(
                                                            yAxisMap,
                                                        ).map(
                                                            ([
                                                                id,
                                                                { name, icon },
                                                            ]) => {
                                                                const handleClick =
                                                                    () => {
                                                                        if (
                                                                            id ===
                                                                            selectedMetric
                                                                        )
                                                                            return;
                                                                        setIsLoadingChart(
                                                                            true,
                                                                        );
                                                                        setIsSelectingFromTable(
                                                                            false,
                                                                        );
                                                                        setSelectedMetric(
                                                                            id,
                                                                        );
                                                                        setSortingOrder(
                                                                            SORTING_ORDER.DESC,
                                                                        );
                                                                    };

                                                                const isSelected =
                                                                    selectedMetric ===
                                                                    id;

                                                                return (
                                                                    <Button
                                                                        key={id}
                                                                        onClick={
                                                                            handleClick
                                                                        }
                                                                        style={{
                                                                            flex: 1,
                                                                            justifyContent:
                                                                                'start',
                                                                            borderColor:
                                                                                'transparent',
                                                                            borderRadius: 0,
                                                                            borderRight:
                                                                                isSelected
                                                                                    ? `2px solid rgba(4, 87, 120, 1)`
                                                                                    : 'transparent',
                                                                            backgroundColor:
                                                                                isSelected
                                                                                    ? colorPrimary
                                                                                    : 'rgba(0,0,0,0.02)',
                                                                        }}
                                                                        type={
                                                                            isSelected
                                                                                ? 'primary'
                                                                                : undefined
                                                                        }
                                                                        icon={
                                                                            <i
                                                                                className={`fa-light fa-${icon as string}`}
                                                                            />
                                                                        }
                                                                    >
                                                                        {name}
                                                                    </Button>
                                                                );
                                                            },
                                                        )}
                                                    </ConfigProvider>
                                                </Flex>
                                            )}

                                            {!noVisuals && (
                                                <div
                                                    style={{
                                                        flex: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {isLoading ||
                                                    isLoadingChart ||
                                                    !chartConfig ? (
                                                        <Flex
                                                            justify="center"
                                                            align="center"
                                                            style={{
                                                                height: 400,
                                                            }}
                                                        >
                                                            <Spin size="large" />
                                                        </Flex>
                                                    ) : visualizationType ===
                                                      'line' ? (
                                                        <Line
                                                            {...chartConfig}
                                                            onReady={chart =>
                                                                (chartRef.current =
                                                                    chart)
                                                            }
                                                        />
                                                    ) : (
                                                        <Column
                                                            {...chartConfig}
                                                            onReady={chart =>
                                                                (chartRef.current =
                                                                    chart)
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Flex>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </Col>
            </Row>
        </ConfigProvider>
    );
};

export default connect(
    ['client', 'filters', 'user'],
    combineActions({ sendNotification }),
)(InsightsChart);
